import {debounce} from './theme.js';

/**
 * Header / search-page predictive search.
 *
 * Three visual states, per Figma "Search", "Search (Typing)" and
 * "Search Suggestions":
 *   - empty   → nothing under the input
 *   - typing  → the "Search for …" quick action, while results are in flight
 *   - results → the two-column suggestions + products panel
 */
export class PredictiveSearch extends HTMLElement {

  /** Breathing room kept between the dropdown and the bottom of the viewport. */
  static VIEWPORT_INSET = 16;

  abortController = undefined;

  constructor() {
    super();

    this.searchTerm = this.input?.value.trim() ?? '';
    this.isOpen = false;
    this.onChange = this.onChange.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.setAvailableHeight = this.setAvailableHeight.bind(this);
    this.debouncedChange = debounce(this.onChange, 300);
  }

  connectedCallback() {
    this.input?.addEventListener('input', this.debouncedChange);
    this.input?.addEventListener('focus', this.onChange);
    document.addEventListener('click', this.handleClickOutside);
    window.addEventListener('resize', this.setAvailableHeight);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.input?.removeEventListener('input', this.debouncedChange);
    this.input?.removeEventListener('focus', this.onChange);
    document.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('resize', this.setAvailableHeight);
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get results() {
    return this.querySelector('#predictive-search');
  }

  get prompt() {
    return this.querySelector('[data-predictive-prompt]');
  }

  get wrapper() {
    return this.querySelector('#predictive-search-wrapper');
  }

  /**
   * Caps the dropdown at the space actually left below it. Measured rather than
   * derived from --announcement-bar-height, which reports the bar's inner text
   * height, not the bar's own height.
   */
  setAvailableHeight() {
    const wrapper = this.wrapper;
    if (!wrapper) { return; }

    const {top} = wrapper.getBoundingClientRect();
    const available = Math.max(0, window.innerHeight - top - PredictiveSearch.VIEWPORT_INSET);
    wrapper.style.setProperty('--predictive-search-available', `${available}px`);
  }

  get hasResults() {
    return Boolean(this.results?.firstElementChild);
  }

  onChange() {
    const newSearchTerm = this.input.value.trim();
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close();
      return;
    }

    // Retarget the quick action, but leave whatever is already on screen alone
    // while the next request is in flight. Tearing the panel down on every
    // keystroke made the dropdown flip between the pill-shaped prompt and the
    // square results panel as the shopper typed.
    this.setPromptTarget(this.searchTerm);

    if (!this.hasResults) { this.revealPrompt(); }

    this.getSearchResults(this.searchTerm);
  }

  /**
   * Points the "Search for …" quick action at the results page for this query.
   * @param {string} searchTerm
   */
  setPromptTarget(searchTerm) {
    const prompt = this.prompt;
    if (!prompt) { return; }

    const template = prompt.dataset.promptTemplate || '';
    prompt.querySelector('[data-predictive-prompt-label]').textContent = template.replace('[terms]', searchTerm);
    prompt.href = `${prompt.pathname}?q=${encodeURIComponent(searchTerm)}`;
  }

  revealPrompt() {
    this.prompt?.classList.remove('hidden');
    this.hideResults();
    this.setAvailableHeight();
  }

  hidePrompt() {
    this.prompt?.classList.add('hidden');
  }

  hideResults() {
    if (this.results) { this.results.innerHTML = ''; }
  }

  /**
   * @param {string} searchTerm
   * @returns {Promise<void>}
   */
  async getSearchResults(searchTerm) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    try {
      const response = await fetch(
        `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(
          searchTerm,
        )}&section_id=predictive-results`,
        {signal: this.abortController.signal},
      );

      if (!response.ok) { throw new Error(`Failed to fetch: ${response.status}`); }

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const markup = html.querySelector('#shopify-section-predictive-results')?.innerHTML ?? '';

      // A stale response can land after the user has already cleared the field.
      if (this.input.value.trim() !== searchTerm) { return; }

      this.updateResults(markup);
    } catch (error) {
      if (error.name === 'AbortError') { return; }
      console.error('Error fetching search results:', error);
      this.close();
    }
  }

  /**
   * @param {string} resultsMarkup
   */
  updateResults(resultsMarkup) {
    // An empty response means the query stopped matching anything — fall back to
    // the quick action rather than leaving the previous query's results up.
    if (!resultsMarkup.trim()) {
      this.revealPrompt();
      return;
    }

    this.results.innerHTML = resultsMarkup;
    this.hidePrompt();
    this.setAvailableHeight();
    this.open();
  }

  open() {
    this.toggleLoading(false);
    this.isOpen = true;
  }

  close() {
    this.abortController?.abort();
    this.toggleLoading(false);
    this.hidePrompt();
    this.hideResults();
    this.isOpen = false;
  }

  clearSearch(event) {
    event.preventDefault();
    this.input.value = '';
    this.close();
  }

  handleClickOutside(event) {
    if (this.isOpen && !this.contains(event.target)) { this.close(); }
  }

  toggleLoading(show) {
    this.querySelector('.predictive-search__loading')?.classList.toggle('hidden', !show);
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
